/**
 * directLightSkip — a per-light early-out in three r185.1's `PhysicalLightingModel.direct()` (the shader sweep,
 * 2026-10-06). Output-identical by construction; an engine optimisation, not an upstream backport.
 *
 * WHY. three's forward path evaluates EVERY scene light's full BRDF for EVERY lit fragment: a GGX D·V·F, Lambert, and
 * the multiple-scattering compensation, which samples the DFG table TWICE (`BRDF_GGX_Multiscatter`: once at N·V, once at
 * N·L). Atlas keeps ~20 punctual lights in that loop — the sun and moon, ten 30 m street lamps, the impact and fire
 * pools (always present at intensity 0: THE LIGHT-SET IDENTITY LAW), cave and lightning lights — and for almost every
 * fragment almost every one of them contributes exactly zero (out of range: three's range cutoff makes the attenuation
 * 0; back-facing: N·L clamps to 0; in shadow: the shadow is folded into the light colour; or simply dark). The
 * contribution is `irradiance × BRDF`, so with `irradiance = 0` it adds 0 whatever the BRDF is.
 *
 * WHAT. (1) The view-only DFG sample (`dfgV`, N·V) is the same for every light: it is built ONCE per material build,
 * placed at the top of the lighting code with `Stack()` (a value first built inside a branch is assigned only on that
 * branch — THE LAZY-SAMPLE LAW), and every light reuses it. (2) Each light's diffuse + specular is wrapped in
 * `If(irradiance > 0)`. The math is r185.1's, op for op, so every fragment a light reaches gets the same bits; a
 * fragment it does not reach adds 0 either way (the one difference: a non-finite BRDF times 0 is NaN in three and
 * nothing here — a fix). The DFG table is a single-mip 16×16 texture and three's WGSL turns derivative uniformity off,
 * so its N·L sample is legal inside the branch on both backends.
 *
 * RECT AREA LIGHTS get the same idea on a UNIFORM branch (the light's own colour): a dark slot of the window pool skips
 * the whole LTC evaluation (see `directRectArea` below).
 *
 * SCOPE. Only the plain model: a material with sheen, clearcoat or iridescence keeps three's own `direct()` (those paths
 * share per-material nodes across lights that the branch would leave unassigned). Subclasses that call `super.direct()`
 * (EON, the ground, skin, cave light) get the skip for three's part and keep their own terms. Pinned to three r185 — the
 * patch copies r185.1's `direct()`, and r186 changed this code (#34046 / #34055); at any other revision it stands down.
 * Kill switch: `localStorage enginex:light-skip = 'off'`, or `installDirectLightSkip({ enabled: false })`.
 */
import * as THREE from 'three/webgpu';
import { Fn, If, Stack, BRDF_GGX, BRDF_Lambert, DFGLUT, normalView, positionViewDirection, roughness, float, vec3, EPSILON } from 'three/tsl';

const REVISION = Number.parseInt(String((THREE as unknown as { REVISION?: string }).REVISION ?? '0'), 10);
/** The patch copies r185.1's direct(); it applies on r185 only. */
export const DIRECT_LIGHT_SKIP_APPLIES = REVISION === 185;

type N = any; // TSL node graphs are untyped here

/** r185.1's `BRDF_GGX_Multiscatter`, with the view-only DFG value passed in (computed once per build). */
const ggxMultiscatter = /*@__PURE__*/ Fn(({ lightDirection, f0, f90, roughness: r, f, USE_IRIDESCENCE, USE_ANISOTROPY, dfgV }: Record<string, N>) => {
  const singleScatter = BRDF_GGX({ lightDirection, f0, f90, roughness: r, f, USE_IRIDESCENCE, USE_ANISOTROPY });
  const dotNL = normalView.dot(lightDirection).clamp();
  const dfgL = DFGLUT({ roughness: r, dotNV: dotNL });
  const FssEssV = f0.mul(dfgV.x).add(f90.mul(dfgV.y));
  const FssEssL = f0.mul(dfgL.x).add(f90.mul(dfgL.y));
  const EssV = dfgV.x.add(dfgV.y);
  const EssL = dfgL.x.add(dfgL.y);
  const EmsV = float(1.0).sub(EssV);
  const EmsL = float(1.0).sub(EssL);
  const Favg = f0.add(f0.oneMinus().mul(0.047619));
  const Fms = FssEssV.mul(FssEssL).mul(Favg).div(float(1.0).sub(EmsV.mul(EmsL).mul(Favg).mul(Favg)).add(EPSILON));
  const compensationFactor = EmsV.mul(EmsL);
  return singleScatter.add(Fms.mul(compensationFactor));
}) as unknown as (inputs: Record<string, N>) => N;

interface DirectInput { lightDirection: N; lightColor: N; reflectedLight: N }
interface RectInput { lightNode?: { colorNode?: N }; reflectedLight: N; [k: string]: N }
interface PhysicalModel {
  sheen?: boolean; clearcoat?: boolean; iridescence?: boolean; anisotropy?: boolean;
  iridescenceFresnel?: N;
  __enginexDfgV?: N;
  __enginexTop?: boolean;
}

export interface DirectLightSkipStats { installed: boolean; reason: string }
let stats: DirectLightSkipStats = { installed: false, reason: 'not installed' };
/** What the install did (for the log and `__lighting`-style levers). */
export function directLightSkipStats(): DirectLightSkipStats { return { ...stats }; }

function storageOff(): boolean {
  try { return (globalThis as { localStorage?: Storage }).localStorage?.getItem('enginex:light-skip') === 'off'; } catch { return false; }
}

/** Install the skip on three's PhysicalLightingModel (global, idempotent). Returns whether it is active. */
export function installDirectLightSkip(opts: { enabled?: boolean } = {}): boolean {
  if (!DIRECT_LIGHT_SKIP_APPLIES) { stats = { installed: false, reason: `three r${REVISION} (the patch is for r185)` }; return false; }
  if (opts.enabled === false || storageOff()) { stats = { installed: false, reason: 'switched off' }; return false; }
  // A strict test mock of three/webgpu THROWS on an export it lacks — a missing class only stands the patch down.
  let proto: Record<string, unknown> | undefined;
  let TSLNS: Record<string, N> | undefined;
  try {
    proto = (THREE as unknown as { PhysicalLightingModel?: { prototype: Record<string, unknown> } }).PhysicalLightingModel?.prototype;
    TSLNS = (THREE as unknown as { TSL?: Record<string, N> }).TSL;
  } catch { proto = undefined; }
  if (!proto || typeof proto.direct !== 'function') { stats = { installed: false, reason: 'no PhysicalLightingModel' }; return false; }
  if (proto.__enginexDirectSkip) { stats = { installed: true, reason: 'already installed' }; return true; }
  const diffuseContribution = TSLNS?.diffuseContribution;
  const specularColorBlended = TSLNS?.specularColorBlended;
  if (!diffuseContribution || !specularColorBlended) { stats = { installed: false, reason: 'property nodes missing' }; return false; }
  const original = proto.direct as (this: PhysicalModel, input: DirectInput, builder: N) => void;
  /**
   * Place what every light's branch shares at the top level of the lighting stack, once per material build. A var is
   * ASSIGNED where it is first built: first built inside a light's branch, the accumulators were re-initialised in every
   * later branch (`directDiffuse = vec3(0)` — only the last lit light survived: the bed's first run, read from its WGSL
   * dump), and a shared accessor would be left unassigned on the pixels whose first branch was skipped.
   */
  const placeShared = (model: PhysicalModel, reflectedLight: N): void => {
    if (model.__enginexTop) return;
    model.__enginexTop = true;
    Stack(reflectedLight.directDiffuse);
    Stack(reflectedLight.directSpecular);
    Stack(normalView);
    Stack(positionViewDirection);
  };
  proto.direct = function (this: PhysicalModel, input: DirectInput, builder: N): void {
    if (this.sheen === true || this.clearcoat === true || this.iridescence === true) { original.call(this, input, builder); return; }
    const { lightDirection, lightColor, reflectedLight } = input;
    placeShared(this, reflectedLight);
    // The view-only DFG value, built once per material build at the first punctual light (top level), shared by all.
    this.__enginexDfgV ??= Stack(DFGLUT({ roughness, dotNV: normalView.dot(positionViewDirection).clamp() }).toVar('enginexDfgV'));
    const dfgV = this.__enginexDfgV;
    const dotNL = normalView.dot(lightDirection).clamp();
    // Placed before the branch, so the condition and the branch read one assignment.
    const irradiance = Stack(dotNL.mul(lightColor).toVar());
    // Skip the BRDF where this light adds nothing (out of range, back-facing, in shadow, dark): irradiance × BRDF = 0.
    If(irradiance.dot(vec3(1)).greaterThan(0), () => {
      reflectedLight.directDiffuse.addAssign(irradiance.mul(BRDF_Lambert({ diffuseColor: diffuseContribution })));
      reflectedLight.directSpecular.addAssign(irradiance.mul(ggxMultiscatter({
        lightDirection, f0: specularColorBlended, f90: 1, roughness, f: this.iridescenceFresnel,
        USE_IRIDESCENCE: this.iridescence, USE_ANISOTROPY: this.anisotropy, dfgV,
      })));
    });
  };
  // Rect area lights: three evaluates the full LTC (two table samples + the polygon integral, twice) for every light on
  // every lit fragment; Atlas's window pool is six, all dark by day. The branch reads the light's own colour UNIFORM
  // (the window node folds its per-pixel room mask into the data it returns, never into this uniform), so it is a
  // uniform branch: every lane agrees, derivatives stay valid (the LTC tables filter linear-mag / nearest-min — a
  // divergent branch could flip that pick), and a lit light runs three's own code unchanged. Clearcoat keeps three's.
  const originalRect = proto.directRectArea as ((this: PhysicalModel, input: RectInput, builder: N) => void) | undefined;
  if (typeof originalRect === 'function') {
    proto.directRectArea = function (this: PhysicalModel, input: RectInput, builder: N): void {
      const base = input.lightNode?.colorNode;
      if (this.clearcoat === true || !base || base.isUniformNode !== true) { originalRect.call(this, input, builder); return; }
      placeShared(this, input.reflectedLight);
      If(base.dot(vec3(1)).greaterThan(0), () => { originalRect.call(this, input, builder); });
    };
  }
  proto.__enginexDirectSkip = true;
  stats = { installed: true, reason: 'r185 PhysicalLightingModel.direct + directRectArea patched' };
  return true;
}
